/**
 * A card while the dashboard is being edited: a placeholder, not the card
 * itself. It shows the type, what its settings choose ("Users", "Kitchen"),
 * and the controls — the drag handle, the width, Settings and Remove.
 *
 * A placeholder rather than the live card, so that a card which renders
 * nothing right now can still be placed, and nothing reloads while it is
 * dragged.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DASHBOARD_LIMITS, type DashboardCardWidth } from '@forsys/shared';
import { useAsync } from '../../hooks/useAsync';
import { MODULE_NAMESPACES } from '../../modules/locales';
import type { DashboardContext, DashboardOption } from '../../modules/types';
import type { ShownCard } from './layout';

const WIDTHS = Array.from({ length: DASHBOARD_LIMITS.COLUMNS }, (_, index) => (index + 1) as DashboardCardWidth);

export interface EditCardProps {
  card: ShownCard;
  ctx: DashboardContext;
  handle: ReactNode;
  onWidth: (width: DashboardCardWidth) => void;
  onSettings: () => void;
  onRemove: () => void;
}

export function EditCard({ card, ctx, handle, onWidth, onSettings, onRemove }: EditCardProps) {
  const { t } = useTranslation(['system', ...MODULE_NAMESPACES]);
  const { type } = card.entry;
  const fields = type.settings ?? [];
  const Icon = type.icon;
  const title = t(type.titleKey);

  const options = useAsync(
    () => Promise.all(fields.map(async (field) => field.options(ctx))),
    [card.entry.key],
    { enabled: fields.length > 0 },
  );

  const labelOf = (option: DashboardOption): string => ('labelKey' in option ? t(option.labelKey) : option.label);
  const summary = options.data
    ? fields
        .map((field, index) => {
          const value = String(card.config[field.key]);
          const option = options.data?.[index]?.find((choice) => choice.value === value);
          return option ? labelOf(option) : t('system:dashboard.unavailable');
        })
        .join(' · ')
    : '';

  const min = type.minWidth ?? 1;
  const max = type.maxWidth ?? DASHBOARD_LIMITS.COLUMNS;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minHeight: 112, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {handle}
        <Box sx={{ display: 'flex', color: 'text.secondary' }}>
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {title}
          </Typography>
          {summary && (
            <Typography variant="caption" color="text.secondary" component="div" noWrap>
              {summary}
            </Typography>
          )}
        </Box>
        {fields.length > 0 && (
          <Tooltip title={t('system:dashboard.settings', { card: title })}>
            <IconButton size="small" aria-label={t('system:dashboard.settings', { card: title })} onClick={onSettings}>
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('system:dashboard.remove', { card: title })}>
          <IconButton size="small" aria-label={t('system:dashboard.remove', { card: title })} onClick={onRemove}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {min < max && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary">
            {t('system:dashboard.width')}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={card.width}
            onChange={(_event, value: DashboardCardWidth | null) => {
              if (value !== null) onWidth(value);
            }}
          >
            {WIDTHS.map((width) => (
              <ToggleButton
                key={width}
                value={width}
                disabled={width < min || width > max}
                aria-label={t('system:dashboard.columns', { count: width })}
                sx={{ px: 1.25, py: 0.25 }}
              >
                {width}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      )}
    </Paper>
  );
}
